import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  Calendar,
  CheckCircle,
  Clock,
  AlertTriangle,
  FileText,
  Users
} from 'lucide-react';
import { TeamMeetingService } from '@/services/team-meeting.service';
import { Meeting, MeetingStatus } from '@/models/team-meeting.model';

export const TeamWorkspace: React.FC = () => {
  const navigate = useNavigate();
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalMeetings: 0,
    pendingApprovals: 0,
    processedThisWeek: 0,
    phiDetected: 0,
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const allMeetings = await TeamMeetingService.getAllMeetings();
      setMeetings(allMeetings.slice(0, 10)); // Latest 10

      // Calculate stats
      const now = new Date();
      const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

      setStats({
        totalMeetings: allMeetings.length,
        pendingApprovals: allMeetings.filter(m =>
          m.status === MeetingStatus.processed
        ).length,
        processedThisWeek: allMeetings.filter(m =>
          m.createdAt >= oneWeekAgo && m.status === MeetingStatus.processed
        ).length,
        phiDetected: allMeetings.filter(m => m.containsPHI).length,
      });
    } catch (error) {
      console.error('Error loading workspace data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: MeetingStatus) => {
    const badges: Record<MeetingStatus, { color: string; label: string; icon: any }> = {
      [MeetingStatus.scheduled]: { color: 'bg-gray-100 text-gray-800', label: 'Scheduled', icon: Calendar },
      [MeetingStatus.processing]: { color: 'bg-blue-100 text-blue-800', label: 'Processing', icon: Clock },
      [MeetingStatus.processed]: { color: 'bg-green-100 text-green-800', label: 'Processed', icon: CheckCircle },
      [MeetingStatus.failed]: { color: 'bg-red-100 text-red-800', label: 'Failed', icon: AlertTriangle },
      [MeetingStatus.archived]: { color: 'bg-gray-100 text-gray-600', label: 'Archived', icon: FileText },
    };

    const badge = badges[status];
    if (!badge) {
      console.error('[TeamWorkspace] Unknown status:', status);
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">Unknown</span>;
    }
    const Icon = badge.icon;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.color}`}>
        <Icon className="w-3 h-3 mr-1" />
        {badge.label}
      </span>
    );
  };

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading workspace...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            <Users className="w-8 h-8 mr-3 text-blue-600" />
            Team Workspace
          </h1>
          <p className="text-gray-600 mt-1">
            AI-powered meeting analysis and decision tracking
          </p>
        </div>
        <button
          onClick={() => navigate('/team-workspace/upload')}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
        >
          <Plus className="w-5 h-5 mr-2" />
          Upload Meeting
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Meetings</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">{stats.totalMeetings}</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-lg">
              <Calendar className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Pending Approvals</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">{stats.pendingApprovals}</p>
            </div>
            <div className="p-3 bg-yellow-100 rounded-lg">
              <Clock className="w-6 h-6 text-yellow-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Processed This Week</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">{stats.processedThisWeek}</p>
            </div>
            <div className="p-3 bg-green-100 rounded-lg">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">PHI Detected</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">{stats.phiDetected}</p>
            </div>
            <div className="p-3 bg-red-100 rounded-lg">
              <AlertTriangle className="w-6 h-6 text-red-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Recent Meetings */}
      <div className="bg-white rounded-lg shadow border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900">Recent Meetings</h2>
        </div>
        <div className="p-6">
          {meetings.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 mb-4">No meetings yet</p>
              <button
                onClick={() => navigate('/team-workspace/upload')}
                className="text-blue-600 hover:text-blue-700 font-medium"
              >
                Upload your first meeting →
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {meetings.map((meeting) => (
                <div
                  key={meeting.id}
                  onClick={() => navigate(`/team-workspace/meetings/${meeting.id}`)}
                  className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:bg-blue-50 cursor-pointer transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {meeting.title}
                        </h3>
                        {getStatusBadge(meeting.status)}
                        {meeting.containsPHI && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            <AlertTriangle className="w-3 h-3 mr-1" />
                            PHI
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-gray-600 space-y-1">
                        <p>
                          <span className="font-medium">Date:</span> {formatDate(meeting.meetingDate)}
                        </p>
                        {meeting.attendees && meeting.attendees.length > 0 && (
                          <p>
                            <span className="font-medium">Attendees:</span>{' '}
                            {meeting.attendees.slice(0, 3).join(', ')}
                            {meeting.attendees.length > 3 && ` +${meeting.attendees.length - 3} more`}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="text-right text-sm text-gray-600">
                      {meeting.status === MeetingStatus.processed && (
                        <div className="space-y-1">
                          <div>{meeting.decisionsCount || 0} decisions</div>
                          <div>{meeting.tasksCount || 0} tasks</div>
                          <div>{meeting.risksCount || 0} risks</div>
                          <div>{meeting.openQuestionsCount || 0} questions</div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
        <button
          onClick={() => navigate('/team-workspace/decisions')}
          className="bg-white rounded-lg shadow p-6 border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-colors text-left"
        >
          <CheckCircle className="w-8 h-8 text-green-600 mb-3" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">View Decisions</h3>
          <p className="text-sm text-gray-600">Browse and approve all extracted decisions</p>
        </button>

        <button
          onClick={() => navigate('/team-workspace/tasks')}
          className="bg-white rounded-lg shadow p-6 border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-colors text-left"
        >
          <FileText className="w-8 h-8 text-blue-600 mb-3" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">View Tasks</h3>
          <p className="text-sm text-gray-600">Manage action items and assignments</p>
        </button>

        <button
          onClick={() => navigate('/team-workspace/risks')}
          className="bg-white rounded-lg shadow p-6 border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-colors text-left"
        >
          <AlertTriangle className="w-8 h-8 text-red-600 mb-3" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">View Risks</h3>
          <p className="text-sm text-gray-600">Track and mitigate identified risks</p>
        </button>
      </div>
    </div>
  );
};

export default TeamWorkspace;
